import { RequestOptions, apis } from "../../backend";
import Call from "../../models/Call";
import { CallFilters } from "../../models/CallFilters";
import LeadList from "../../models/LeadList";
import LeadListQuery from "../../models/LeadListQuery";
import PhoneNumber from "../../models/PhoneNumber";
import { SimpleCall } from "../../models/SimpleCall";

/**
 * Fetches calls.
 */
export async function listCalls(query: CallFilters, opts?: RequestOptions): Promise<Call[]> {
  const { calls } = await apis.dial.get('/calls-v1',
    query.toQuery(), { overflowQueryToHeader: true, ...opts });

  return calls.map((call: Partial<Call>) => new Call(call));
}

export async function listSimpleCalls(query: CallFilters, opts?: RequestOptions): Promise<SimpleCall[]> {
  const calls = await apis.dial.get<SimpleCall[]>('/call-information-v1/simple',
    query.toQuery(), { overflowQueryToHeader: true, ...opts });

  return calls.map(call => new SimpleCall(call));
}

export async function countCalls(query: CallFilters, options?: Pick<RequestOptions, 'signal'>): Promise<number> {
  const { count } = await apis.dial.get('/calls-v1/count', {
    ...(query || {}),
    ...(query.sort?.toQuery() || {}),
    count: undefined,
    offset: undefined,
    sort: undefined,
    s: query.search,
  }, { ...options, overflowQueryToHeader: true });

  return count;
}

export type CallUpdate = Partial<Call> & {
  evaluatedAt?: Date;
  addTagIds?: string[];
  removeTagIds?: string[];
  debug?: string[];
  evaluationTagIds?: string[];
  evaluationAuto?: 'offer';
};

/**
 * Updates an existing call.
 */
export async function updateCall(callId: string, update: CallUpdate) {
  await apis.dial.put(`/calls-v1/${callId}`, update);
}

/**
 * Deletes an existing call.
 */
export async function deleteCall(callId: string) {
  await apis.dial.del(`/calls-v1/${callId}`);
}

/**
 * Creates a pending call to allow executing actions etc. early.
 */
export async function createPendingCall(call: Call): Promise<Call> {
  const result = await apis.dial.post('/calls-v1/pending', {
    ...call,
    destinationPhoneNumber: call.recipient,
    callerPhoneNumber: call.caller,
  });
  return new Call(result);
}

/**
 * Fetches lead lists.
 */
export async function listLeadLists(query: LeadListQuery, options?: RequestOptions): Promise<LeadList[]> {
  const leadLists = await apis.dial.get<LeadList[]>('/dial-groups-v2', {
    ...query,
    ...(query.sort?.toQuery() || {}),
    sort: undefined,
  }, options);

  return leadLists.map((group: LeadList) => new LeadList(group));
}

/**
 * Fetches lead lists.
 */
export async function countLeadLists(query: LeadListQuery): Promise<number> {
  const { count } = await apis.dial.get('/dial-groups-v1/count', {
    ...query,
    ...(query.sort?.toQuery() || {}),
    sort: undefined,
  });

  return count;
}

/**
 * Fetches a specific lead lists.
 */
export async function getLeadList(leadListId: string): Promise<LeadList> {
  const leadList = await apis.dial.get<LeadList>(`/dial-groups-v2/${leadListId}`);

  return new LeadList(leadList);
}

/**
 * Updates a specific lead list.
 */
export async function updateLeadList(leadListId: string, update: Partial<LeadList>) {
  await apis.dial.put(`/dial-groups-v1/${leadListId}`, update);
}

/**
 * Adds contacts to a lead list.
 */
export async function addLeadListContacts(leadListId: string, contactIds: string[]) {
  await apis.dial.post(`/dial-group-contacts-v1/${leadListId}`, { contactIds });
}

/**
 * Deletes a specific lead list.
 * @param leadListId ID of the lead list to delete
 * @param unlinkContacts Whether to unlink contacts from calls and other lead lists
 */
export async function deleteLeadList(leadListId: string, unlinkContacts?: boolean) {
  await apis.dial.del(`/dial-groups-v1/${leadListId}`, { unlinkContacts });
}

export type SimpleLeadList = Pick<LeadList, 'id' | 'name'>;

export async function listAllLeadLists(query?: LeadListQuery, options?: RequestOptions) {
  query = query ?? undefined;

  const leadLists = await apis.dial.get<SimpleLeadList[]>('/dial-group-information-v1/all', {
    ...query,
    ...(query?.sort?.toQuery() ?? {}),
    sort: undefined,
  }, options);

  return leadLists;
}

type PhoneNumberFilters = {
  /**
   * Admin only.
   */
  organizationId?: string;
};

/**
 * Lists all phone numbers.
 */
export async function listPhoneNumbers(filters: PhoneNumberFilters): Promise<PhoneNumber[]> {
  const phoneNumbers = await apis.dial.get<PhoneNumber[]>(`/phone-numbers-v1`, filters);

  return phoneNumbers.map(phoneNumber => new PhoneNumber(phoneNumber));
}